import type { YearSummary } from '@flightlog/core';
import { countryFlagUrl, countryName } from './countries';
import { plural } from './format';

// Картинка «Год в полётах» 1080×1350 (4:5, как пост) — всегда тёмная, чтобы одинаково
// смотрелась у любого, кому её перешлют.

const W = 1080;
const H = 1350;
const PAD = 88;
const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const EARTH_KM = 40_075;
const MONTHS_IN = [
  'январе',
  'феврале',
  'марте',
  'апреле',
  'мае',
  'июне',
  'июле',
  'августе',
  'сентябре',
  'октябре',
  'ноябре',
  'декабре',
];

const n = (value: number) => Math.round(value).toLocaleString('ru-RU');

/** SVG флага без размеров рисуется в canvas непредсказуемо — задаём их явно. */
async function loadFlag(code: string): Promise<HTMLImageElement | null> {
  const url = countryFlagUrl(code);
  if (!url) return null;
  try {
    const svg = (await (await fetch(url)).text()).replace('<svg ', '<svg width="640" height="480" ');
    const src = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    try {
      const image = new Image();
      image.src = src;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(src);
    }
  } catch {
    return null;
  }
}

function fit(context: CanvasRenderingContext2D, text: string, width: number): string {
  if (context.measureText(text).width <= width) return text;
  let cut = text;
  while (cut.length > 1 && context.measureText(`${cut}…`).width > width) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
}

function roundRect(context: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + w, y, x + w, y + h, r);
  context.arcTo(x + w, y + h, x, y + h, r);
  context.arcTo(x, y + h, x, y, r);
  context.arcTo(x, y, x + w, y, r);
  context.closePath();
}

function rows(summary: YearSummary): [string, string][] {
  const list: [string, string][] = [];
  list.push(['Облёт Земли', `×${(summary.distanceKm / EARTH_KM).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}`]);
  if (summary.longest) {
    list.push([
      'Самый длинный',
      `${summary.longest.depIata} → ${summary.longest.arrIata} · ${n(summary.longest.distanceKm)} км`,
    ]);
  }
  if (summary.topAirport) {
    list.push(['Любимый аэропорт', `${summary.topAirport.iata} · ${summary.topAirport.city}`]);
  }
  if (summary.newCountries.length > 0) {
    list.push(['Новые страны', summary.newCountries.map(countryName).join(', ')]);
  }
  if (summary.busiestMonth) {
    list.push([
      'Больше всего',
      `в ${MONTHS_IN[summary.busiestMonth.month - 1]} · ${summary.busiestMonth.flights} ${plural(summary.busiestMonth.flights, ['рейс', 'рейса', 'рейсов'])}`,
    ]);
  }
  if (summary.previous) {
    const diff = summary.flights - summary.previous.flights;
    list.push([
      `К ${summary.year - 1}`,
      diff === 0
        ? 'столько же рейсов'
        : `${diff > 0 ? '+' : '−'}${Math.abs(diff)} ${plural(Math.abs(diff), ['рейс', 'рейса', 'рейсов'])}`,
    ]);
  }
  return list.slice(0, 4);
}

export async function drawYearCard(summary: YearSummary): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const context = canvas.getContext('2d');
  if (!context) return canvas;

  const background = context.createLinearGradient(0, 0, W, H);
  background.addColorStop(0, '#13264a');
  background.addColorStop(1, '#090e1a');
  context.fillStyle = background;
  context.fillRect(0, 0, W, H);

  // Тонкие дуги маршрутов на фоне.
  context.strokeStyle = 'rgba(110, 168, 254, 0.16)';
  context.lineWidth = 3;
  for (const [x1, x2, lift] of [
    [600, 1180, 220],
    [680, 1240, 340],
    [500, 1120, 420],
  ] as const) {
    context.beginPath();
    context.moveTo(x1, 460);
    context.quadraticCurveTo((x1 + x2) / 2, 460 - lift, x2, 460);
    context.stroke();
  }

  context.textBaseline = 'alphabetic';
  context.fillStyle = '#8fb4ff';
  context.font = `600 34px ${FONT}`;
  context.fillText('✈  JETLOG', PAD, 150);

  context.fillStyle = '#ffffff';
  context.font = `800 210px ${FONT}`;
  context.fillText(String(summary.year), PAD - 8, 350);
  context.fillStyle = '#c9d4e8';
  context.font = `500 54px ${FONT}`;
  context.fillText('год в полётах', PAD, 425);

  const hours = summary.durationMin / 60;
  const cells: [string, string][] = [
    [n(summary.flights), plural(summary.flights, ['рейс', 'рейса', 'рейсов'])],
    [n(summary.distanceKm), 'км'],
    [`${summary.estimated ? '≈' : ''}${n(hours)}`, `${plural(Math.round(hours), ['час', 'часа', 'часов'])} в воздухе`],
    [n(summary.countries.length), plural(summary.countries.length, ['страна', 'страны', 'стран'])],
  ];
  const gap = 32;
  const cellW = (W - PAD * 2 - gap) / 2;
  const cellH = 180;
  const gridTop = 490;
  cells.forEach(([value, label], index) => {
    const x = PAD + (index % 2) * (cellW + gap);
    const y = gridTop + Math.floor(index / 2) * (cellH + gap);
    context.fillStyle = 'rgba(255, 255, 255, 0.06)';
    roundRect(context, x, y, cellW, cellH, 28);
    context.fill();
    context.fillStyle = '#ffffff';
    context.font = `700 92px ${FONT}`;
    context.fillText(fit(context, value, cellW - 64), x + 32, y + 104);
    context.fillStyle = '#8fa3c4';
    context.font = `600 28px ${FONT}`;
    context.fillText(label.toUpperCase(), x + 32, y + 150);
  });

  let y = gridTop + cellH * 2 + gap + 80;
  for (const [label, value] of rows(summary)) {
    context.fillStyle = '#8fa3c4';
    context.font = `500 30px ${FONT}`;
    context.fillText(label, PAD, y);
    const labelWidth = context.measureText(label).width;
    context.fillStyle = '#ffffff';
    context.font = `700 36px ${FONT}`;
    context.textAlign = 'right';
    context.fillText(fit(context, value, W - PAD * 2 - labelWidth - 40), W - PAD, y);
    context.textAlign = 'left';
    context.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(PAD, y + 26);
    context.lineTo(W - PAD, y + 26);
    context.stroke();
    y += 68;
  }

  const flags = (await Promise.all(summary.countries.slice(0, 10).map(loadFlag))).filter(
    (image): image is HTMLImageElement => image !== null,
  );
  const flagW = 66;
  const flagH = 50;
  const flagTop = H - 150;
  flags.forEach((image, index) => {
    const x = PAD + index * (flagW + 14);
    context.save();
    roundRect(context, x, flagTop, flagW, flagH, 6);
    context.clip();
    context.drawImage(image, x, flagTop, flagW, flagH);
    context.restore();
  });
  if (summary.countries.length > flags.length && flags.length > 0) {
    context.fillStyle = '#8fa3c4';
    context.font = `600 30px ${FONT}`;
    context.fillText(`+${summary.countries.length - flags.length}`, PAD + flags.length * (flagW + 14), flagTop + 36);
  }

  context.fillStyle = '#5f7396';
  context.font = `500 26px ${FONT}`;
  context.fillText('@wtfaviabot', PAD, H - 50);

  return canvas;
}
